import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import {
  Button, PageShell, Skeleton, DataTable, StatusBadge, Pagination, type DataTableColumn,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui';
import { Plus } from 'lucide-react';
import { useOfflineStoresQuery, useOfflineStoreMutation } from '../hooks/useOfflineStoresQuery';

const PAGE_SIZE = 20;

type OfflineStoreRow = {
  id: number;
  name: string;
  address: string;
  storeKey: string;
  sortOrder: number;
  isActive: boolean;
};

export const OfflineStoreListPage = () => {
  const { t } = useTranslation('common');
  const [page, setPage] = useState(0);
  const query = useOfflineStoresQuery(page, PAGE_SIZE);
  const { remove } = useOfflineStoreMutation();
  const [deleteTarget, setDeleteTarget] = useState<OfflineStoreRow | null>(null);

  const items = (query.data?.content ?? []) as OfflineStoreRow[];
  // 총 건수는 서버가 준 totalElements 를 그대로 쓴다. (totalPages × PAGE_SIZE 로 역산하면
  // 마지막 페이지가 가득 차지 않을 때 실제보다 과대 표기된다.)
  const totalElements = query.data?.totalElements ?? 0;

  // QA-409 — No(일련번호) · 매장명(클릭 → 상세) · 주소(넓게) · 활성 · [삭제]. 수기 "순서" 열과 ⋯ 메뉴는 뺐다.
  const columns: DataTableColumn<OfflineStoreRow>[] = [
    {
      key: 'no',
      header: 'No',
      align: 'center',
      width: 64,
      cell: (_row, i) => (
        <span className="text-xs tabular-nums" style={{ color: 'var(--content-muted)' }} data-testid="store-row-no">
          {totalElements - (page * PAGE_SIZE + i)}
        </span>
      ),
    },
    {
      key: 'name',
      header: t('cms.stores.cols.name'),
      width: 280,
      cell: (row) => (
        <Link
          to={`/cms/offline-stores/${row.id}`}
          className="block max-w-[260px] truncate text-sm font-medium hover:underline"
          style={{ color: 'var(--content-strong)' }}
          title={row.name}
          data-testid="store-name-link"
        >
          {row.name}
        </Link>
      ),
    },
    {
      key: 'address',
      header: t('cms.stores.cols.address'),
      cell: (row) => (
        <span className="block text-sm" style={{ color: 'var(--content-default)' }} title={row.address}>
          {row.address}
        </span>
      ),
    },
    {
      key: 'isActive',
      header: t('cms.stores.cols.isActive'),
      align: 'center',
      width: 96,
      cell: (row) => (
        <StatusBadge
          variant={row.isActive ? 'success' : 'info'}
          label={row.isActive ? t('cms.active') : t('cms.inactive')}
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: 80,
      cell: (row) => (
        <Button variant="outline" size="sm" onClick={() => setDeleteTarget(row)} data-testid="store-delete">
          {t('cms.delete')}
        </Button>
      ),
    },
  ];

  return (
    <PageShell
      title={t('cms.stores.title')}
      actions={
        <Link to="/cms/offline-stores/new">
          <Button size="sm">
            <Plus size={15} aria-hidden="true" />
            {t('cms.stores.create')}
          </Button>
        </Link>
      }
      data-testid="offline-store-list-page"
    >
      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[44px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('cms.stores.loadFailed')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('cms.retry')}
          </Button>
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={items}
          rowKey={(row) => row.id}
          emptyState={t('cms.stores.empty')}
          stickyHeader
        />
      )}

      <Pagination
        total={totalElements}
        currentPage={page + 1}
        pageSize={PAGE_SIZE}
        onPageChange={(p) => setPage(p - 1)}
        className="mt-4"
      />

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('cms.stores.title')} 삭제</DialogTitle>
            <DialogDescription>
              {t('cms.stores.confirmDelete', { name: deleteTarget?.name ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('cms.cancel')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={remove.isPending}
              onClick={() => {
                if (deleteTarget) remove.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
              }}
            >
              {t('cms.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
